import { useMemo } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventWebsiteDesignQuery } from './useEventWebsiteDesign.graphql.generated';
import { fromEvent, LOADING } from './useEventWebsiteDesign.utils';
import type { EventWebsiteDesign } from './useEventWebsiteDesign.types';

/**
 * The current event's website design in V1 shape, for surfaces outside the website editor that
 * match or tint against it (card shop, dashboard suite, …). Callers never deal with website
 * versions or sites: one request returns the live charm design and, for a Website V2 event only,
 * its active carbon site (the one withjoy.com/:handle serves), which then wins. See
 * doc/website-v2/legacy-design-compat.md.
 *
 * The event is `eventHandleOverride` when given (`''` for none), else the one `useEventInfo` resolves (the route's event).
 */
export const useEventWebsiteDesign = (eventHandleOverride?: string): EventWebsiteDesign => {
  const eventInfo = useEventInfo();
  const eventHandle = eventHandleOverride ?? eventInfo.eventHandle;
  const { data, loading } = useEventWebsiteDesignQuery({
    variables: { eventHandle: eventHandle ?? '' },
    batchMode: 'fast',
    skip: !eventHandle,
  });
  return useMemo(() => (loading && !data ? LOADING : fromEvent(data?.eventByName)), [loading, data]);
};
